'use client';

import { useMemo } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileFrontEndTimelineView,
  type MobileUiConfigArtifact,
  type MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  buildTimelineQuery,
  mapTimelineEvents,
  mergeTimelineEvents,
  timelineSources,
  type TimelineEvent,
  type TimelineSource,
} from '../lib/build-timeline-query';

/** Artifact types fanned out for a merged timeline. Each one is a request. */
export const MAX_TIMELINE_SOURCES = 8;

/** Rows pulled from each source in merged mode. */
const MERGED_PAGE_LIMIT = 200;

/** Entries kept after merging, so a dense acquisition can't stall the list. */
const MAX_MERGED_EVENTS = 500;

export interface TimelineResult {
  events: TimelineEvent[];
  /** Total rows the server reports, summed across sources. */
  total: number;
  /** Types that contributed, for the legend. */
  sources: { graphqlType: string; typeLabel: string; count: number }[];
  /** True when entries were dropped at the merge cap. */
  truncated: boolean;
}

/**
 * Timeline entries for one artifact type, or merged across several.
 *
 * Merged mode is bounded twice — by source count and by merged entries — for the
 * same reason `useMapPoints` is: an acquisition can hold millions of rows, and a
 * timeline that tries to be complete is a timeline nobody can wait for. The merge
 * is a window on the most recent activity, not an exhaustive record, and the UI
 * says so when it truncates.
 */
export function useTimelineEvents(args: {
  mobileAcquisitionId: number;
  uiConfig: MobileUiConfigResponse | undefined;
  /** Single-type mode. Omit for a merged timeline. */
  single?: {
    graphqlType: string;
    typeLabel: string;
    artifact: MobileUiConfigArtifact;
    view: MobileFrontEndTimelineView;
  };
  /** Merged mode: restrict to these artifact families. */
  families?: readonly string[];
  direction: 'asc' | 'desc';
  offset: number;
  limit: number;
  where: Record<string, unknown> | null;
  /** When false the query stays idle. Defaults to on. */
  active?: boolean;
}): UseQueryResult<TimelineResult, Error> {
  const { mobileAcquisitionId, uiConfig, single, families, direction, offset, limit, where } =
    args;
  const { cellId, accessToken, enabled } = useCellServiceSession();

  const sources = useMemo<TimelineSource[]>(() => {
    if (single) {
      const rootQuery = single.artifact.front_end?.graphql_root_query;
      if (!rootQuery) return [];
      return [
        {
          graphqlType: single.graphqlType,
          typeLabel: single.typeLabel,
          rootQuery,
          view: single.view,
          artifact: single.artifact,
        },
      ];
    }
    return timelineSources(uiConfig, { families, limit: MAX_TIMELINE_SOURCES });
  }, [single, uiConfig, families]);

  const merged = !single;

  return useQuery<TimelineResult, Error>({
    queryKey: mobileKeys.artifactList(
      cellId,
      mobileAcquisitionId,
      single?.graphqlType ?? '__merged__',
      {
        view: 'timeline',
        sources: sources.map((s) => s.graphqlType),
        direction,
        offset: merged ? 0 : offset,
        limit,
        where: merged ? null : where,
      },
    ),
    enabled: enabled && args.active !== false && sources.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const pages = await Promise.all(
        sources.map(async (source) => {
          const query = buildTimelineQuery({
            rootQuery: source.rootQuery,
            view: source.view,
            artifact: source.artifact,
            mobileAcquisitionId,
            // Merged mode can't page a stream it assembles client-side: entry N
            // of the merged order isn't entry N of any one source.
            offset: merged ? 0 : offset,
            limit: merged ? MERGED_PAGE_LIMIT : limit,
            direction,
            // A filter is written against one type's fields, so it can only
            // apply when the timeline is showing that type.
            where: merged ? null : where,
          });
          if (!query) return { source, events: [], total: 0 };

          const data = await mobileGraphql<
            Record<string, MobileConnection<Record<string, unknown>>>
          >({
            cellId: cellId as string,
            accessToken: accessToken as string,
            endpoint: source.artifact.endpoint,
            query,
            signal,
          });

          const connection = data[source.rootQuery];
          const events = mapTimelineEvents(connection?.items ?? [], {
            view: source.view,
            graphqlType: source.graphqlType,
            typeLabel: source.typeLabel,
          });
          return { source, events, total: connection?.total ?? 0 };
        }),
      );

      const cap = merged ? MAX_MERGED_EVENTS : limit;
      const events = mergeTimelineEvents(
        pages.map((page) => page.events),
        direction,
        cap,
      );

      return {
        events,
        total: pages.reduce((sum, page) => sum + page.total, 0),
        sources: pages.map((page) => ({
          graphqlType: page.source.graphqlType,
          typeLabel: page.source.typeLabel,
          count: page.events.length,
        })),
        truncated: pages.reduce((sum, page) => sum + page.events.length, 0) > events.length,
      };
    },
  });
}
